import { RequestDetailsHeader, RequestDetailsSection } from "../RequestDetailsContent";
import {
  RequestCardReason,
  RequestCardSection,
  RequestInfoItem,
  RequestInfoGrid,
  RequestCardHeader,
  RequestStatusStrip,
} from "../RequestCardContent";
import React, { useEffect, useMemo, useState } from 'react'
import { ResourceRequestsProps, webURLResponseProps } from '../resourceRequestInterface'
import ResourceRequestToolbar from "../ResourceRequestToolbar";
import { myLocalStorage } from '../../../StorageHelper';
import useUserStore from '../../../../services/userStore';
import { AlertTriangle, Calendar, CheckCircle2, Clock, Eye, Globe, RotateCcw, User, XCircle } from 'lucide-react';
import { getWebURLNotificationAPI, getWebURLNotificationCountAPI, revokeRequest, updateRequest } from '../Api/resourcerequestapi';
import { format } from "date-fns";
import { Card, CardContent } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Dialog, DialogContent } from "../../../ui/dialog";
import { getStatusColor } from '../utils';
import { Skeleton } from '../../interfaces/CommonInterfaces';
import { formatDate } from '../../formatTimeAgo';
import { Badge } from '../../../ui/badge';


export const WebUrlRequestDetails = ({
  computerName = "",
  searchTerm = "",
  statusFilter = "",
  privilegeFilter = "all",
  startDate,
  endDate,
  activeTab,
  onSearchChange
}: ResourceRequestsProps) => {
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<webURLResponseProps[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<webURLResponseProps | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const tenantName = useMemo(
    () => myLocalStorage.getItem("latestTenant").tenantName || "",
    []
  );
  const { email, role, userRole } = useUserStore((state) => state.user);
  const [loadingId, setLoadingId] = useState<number | null>(null);

  const handleViewDetails = (request: webURLResponseProps) => {
    setSelectedRequest(request);
    setIsDetailsOpen(true);
  };

  const handleRequest = async (status: string, request: webURLResponseProps) => {
    setLoadingId(request.id)
    let payload = {
      accessId: request.id,
      requestStatus: status,
      accessType: request.accessType,
    }
    await updateRequest(tenantName, payload)
    fetchWebNotification(false);
    setLoadingId(null)
  }


  const handleRevoke = async (accessId: number) => {
    await revokeRequest("weburl", accessId)
    fetchWebNotification(false);
  }

  const fetchWebURLCount = async () => {
    if (userRole === "epam-admin") return;

    const response = await getWebURLNotificationCountAPI(
      computerName,
      searchTerm,
      statusFilter.toUpperCase(),
      startDate ? format(startDate, "yyyy-MM-dd") : "",
      endDate ? format(endDate, "yyyy-MM-dd") : "",
      tenantName,
      page,
      email,
      role,
        privilegeFilter
      )
    setTotalItems(response || 0);
  }

  const fetchWebNotification = async (isFirst = true) => {
    if (userRole === "epam-admin") return;
    setIsLoading(isFirst);
    try {
      const response: webURLResponseProps[] = await getWebURLNotificationAPI(
        computerName,
        searchTerm,
        statusFilter.toUpperCase(),
        startDate ? format(startDate, "yyyy-MM-dd") : "",
        endDate ? format(endDate, "yyyy-MM-dd") : "",
        tenantName,
        page - 1,
        email,
        role,
      privilegeFilter
    );
      setNotifications(response);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchWebURLCount();
    fetchWebNotification();
    if (activeTab === "weburl") {
      const interval = setInterval(() => {
        fetchWebURLCount();
        fetchWebNotification(false);
      }, 2000)
      return () => clearInterval(interval)
    }
  }, [
    computerName,
    searchTerm,
    statusFilter,
    privilegeFilter,
    startDate,
    endDate,
    tenantName,
    page,
    email,
    role,
    activeTab
  ]);
  const totalPages = Math.ceil(totalItems / 15);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "PENDING":
        return <Clock className="w-3 h-3" />;
      case "APPROVED":
        return <CheckCircle2 className="w-3 h-3" />;
      case "DECLINED":
        return <XCircle className="w-3 h-3" />;
      case "REVOKED":
        return <RotateCcw className="w-3 h-3" />;
      case "EXPIRED":
        return <AlertTriangle className="w-3 h-3" />;
      default:
        return <Clock className="w-3 h-3" />;
    }
  };

  return (
    <div className="resource-request-list space-y-1">
      {onSearchChange && (
        <ResourceRequestToolbar
          page={page}
          totalItems={totalItems}
          onPageChange={setPage}
          searchTerm={searchTerm}
          onSearchChange={(value) => {
            setPage(1);
            onSearchChange(value);
          }}
        />
      )}
      {isLoading ?
        <>
          {[...Array(1)].map((_, idx) => (
            <Card key={idx} className="request-card border-slate-200 dark:border-slate-700">
              <CardContent className="request-card-content p-4 animate-pulse">
                <div className="flex justify-between items-center mb-3">
                  <Skeleton className="h-4 w-32 rounded" />
                  <Skeleton className="h-6 w-20 rounded" />
                </div>
                <Skeleton className="h-3 w-full rounded mb-2" />
                <Skeleton className="h-3 w-3/4 rounded mb-2" />
                <Skeleton className="h-3 w-1/2 rounded" />
                <div className="mt-4 flex justify-between">
                  <Skeleton className="h-6 w-28 rounded" />
                  <Skeleton className="h-6 w-28 rounded" />
                </div>
              </CardContent>
            </Card>
          ))}

        </> : notifications.length > 0 ? (
          <>

            {/* Request Cards */}
            {notifications.map((request) => (
              <Card
                key={request.id}
                className="request-card border-slate-200 dark:border-slate-700"
                data-testid={`weburl-request-card-${request.id}`}
              >
                <RequestStatusStrip status={request.requestStatus} />
                <CardContent className="request-card-content p-3">
                  <div className="flex items-start gap-3">
                    <div className="request-card-body flex-1 space-y-1">
                      {/* Header */}
                      <RequestCardHeader
                        icon={<Globe className="w-3.5 h-3.5 text-indigo-500" />}
                        title={<>{new URL(request.accessUrl).hostname}</>}
                        status={
                          <Badge
                            className={`border text-xs px-1.5 py-0.5 ${getStatusColor(request.requestStatus)}`}
                          >
                            <div className="flex items-center gap-1">
                              {getStatusIcon(request.requestStatus)}
                              {request.requestStatus}
                            </div>
                          </Badge>
                        }
                        subtitle={
                          <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-300">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(request.createdAt)}
                            </div>
                            <div className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {request.email}
                            </div>
                          </div>
                        }
                        actions={
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDetails(request)}
                            className="h-auto px-2 py-1 text-xs"
                            data-testid={`button-details-weburl-${request.id}`}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Details
                          </Button>
                        }
                      />

                      {/* Content */}
                      <RequestInfoGrid className="grid-cols-2">
                        <RequestInfoItem label="URL">
                          <div className="font-medium truncate text-slate-700 dark:text-slate-200">
                            {request.accessUrl}
                          </div>
                        </RequestInfoItem>
                        <RequestInfoItem label="Credential">
                          <div className="font-medium text-slate-700 dark:text-slate-200">
                            {request.accessCredential}
                          </div>
                        </RequestInfoItem>

                        {request.privilegeLevel === "PERMANENT" ? (
                          <RequestInfoItem label="Access Type">
                            <div className="font-medium text-slate-700 dark:text-slate-200">
                              Permanent
                            </div>
                          </RequestInfoItem>
                        ) : (
                          <RequestInfoItem label="Privilege">
                            <div className="font-medium text-slate-700 dark:text-slate-200">
                              {request.privilegeLevel}
                            </div>
                          </RequestInfoItem>
                        )}

                        {request.schedules.length === 0 && request.selectedTime !== 0 && (
                          <RequestInfoItem label="Duration">
                            <div className="font-medium text-slate-700 dark:text-slate-200">
                              {request.privilegeLevel === "PERMANENT"
                                ? "Permanent"
                                : `${request.selectedTime} min`}
                            </div>
                          </RequestInfoItem>
                        )}
                      </RequestInfoGrid>

                      {request.schedules?.length > 0 && (
                        <RequestCardSection variant="schedule">
                          <RequestInfoGrid className="grid-cols-2" context>
                            <RequestInfoItem label="Start Time">
                              <p className="text-blue-800 dark:text-blue-200">
                                {formatDate(request.schedules[0].scheduledStartTime)}
                              </p>
                            </RequestInfoItem>
                            <RequestInfoItem label="End Time">
                              <p className="text-blue-800 dark:text-blue-200">
                                {formatDate(request.schedules[0].scheduledEndTime)}
                              </p>
                            </RequestInfoItem>
                          </RequestInfoGrid>
                        </RequestCardSection>
                      )}

                      {/* Admin Actions History */}
                      {((request.approvalEmailId?.length ?? 0) > 0 || request.revokedBy) && (
                        <RequestCardSection variant="history">
                          <div className="space-y-1 text-sm">
                            {request.approvalEmailId.length > 0 && (
                              <p className="text-green-700 dark:text-green-300">
                                ✓ Approved by {request.approvalEmailId[0]} on{" "}
                                {formatDate(request.createdAt)}
                              </p>
                            )}
                            {request.revokedBy && (
                              <p className="text-purple-700 dark:text-purple-300">
                                ⊝ Revoked by {request.revokedBy}
                              </p>
                            )}
                          </div>
                        </RequestCardSection>
                      )}

                      {/* Actions */}
                      <div className="request-card-footer flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700">
                        <RequestCardReason>{request.requestMessage}</RequestCardReason>
                        <div className="flex items-center gap-2">
                          {/* Conditional Action Buttons */}
                          {request.requestStatus === "PENDING" && (
                            <>
                              {role !== "TENANT_USER" ? (
                                <>
                                  <Button
                                    disabled={loadingId === request.id}
                                    size="sm"
                                    onClick={() => handleRequest("APPROVED", request)}
                                    className="text-white bg-green-600 hover:bg-green-700"
                                    data-testid={`button-approve-weburl-${request.id}`}
                                  >
                                    {loadingId === request.id ? (
                                      <>
                                        <svg
                                          className="animate-spin w-4 h-4 mr-1 text-white"
                                          xmlns="http://www.w3.org/2000/svg"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                        >
                                          <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                          ></circle>
                                          <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                                          ></path>
                                        </svg>
                                        Approving...
                                      </>
                                    ) : (
                                      <>
                                        <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                                      </>
                                    )}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handleRequest("DECLINE", request)}
                                    data-testid={`button-decline-weburl-${request.id}`}
                                  >
                                    <XCircle className="w-4 h-4 mr-1" />
                                    Decline
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    className="bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800"
                                    onClick={() => setIsDetailsOpen?.(false)}
                                  >
                                    <CheckCircle2 className="w-4 h-4 mr-1" /> Pending
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                                    onClick={() => {
                                      handleRevoke(request.id);
                                    }}
                                  >
                                    <RotateCcw className="w-4 h-4 mr-1" />{" "}
                                    {role === "TENANT_USER" ? "Cancel Request" : "Revoke"}
                                  </Button>
                                </>
                              )}
                            </>
                          )}

                          {request.requestStatus === "APPROVED" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                handleRevoke(request.id);
                              }}
                              className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                              data-testid={`button-revoke-weburl-${request.id}`}
                            >
                              <RotateCcw className="w-4 h-4 mr-1" />
                              {role === "TENANT_USER" ? "Cancel Request" : "Revoke"}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

          </>
        ) : (
          <Card className="request-card request-empty-state border-slate-200 dark:border-slate-700">
            <CardContent className="request-card-content p-12 text-center">
              <Globe className="w-12 h-12 mx-auto mb-4 text-indigo-300 dark:text-indigo-600" />
              <h3 className="mb-2 text-lg font-medium text-slate-600 dark:text-slate-300">
                No WebURL Requests Found
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                No web application access requests match your current filters.
              </p>
            </CardContent>
          </Card>
        )}

      {!onSearchChange && totalPages > 1 && (
        <div className="flex items-center justify-between py-4">
          <div className="text-sm text-slate-600 dark:text-slate-300">
            Showing {(page - 1) * 15 + 1}–{Math.min(page * 15, totalItems)} of {totalItems}
          </div>

          <div className="flex items-center space-x-2">
            {/* Previous Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              data-testid="button-prev-page-onprem"
            >
              Previous
            </Button>

            {/* Dynamic Page Numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const startPage = Math.max(1, Math.min(page - 2, totalPages - 4));
              const pageNum = startPage + i;

              return (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPage(pageNum)}
                  className={pageNum === page ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
                  data-testid={`page-onprem-${pageNum}`}
                >
                  {pageNum}
                </Button>
              );
            })}

            {/* Next Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              data-testid="button-next-page-onprem"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Details Modal */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="request-details-dialog">
          <RequestDetailsHeader
            icon={<Globe />}
            title="Web Application Access Request Details"
          >
            Complete details for the web application access request
          </RequestDetailsHeader>
          {selectedRequest && (
            <div className="request-details-body">
              <RequestDetailsSection title="Request information" variant="request">
                <RequestInfoGrid className="request-details-grid">
                  <RequestInfoItem label="Request ID">
                    <p className="text-slate-800 dark:text-slate-100">
                      {selectedRequest.id}
                    </p>
                  </RequestInfoItem>
                  <RequestInfoItem label="Status">
                    <p className="text-slate-800 dark:text-slate-100">
                      {selectedRequest.requestStatus}
                    </p>
                  </RequestInfoItem>
                  <RequestInfoItem label="Requested By">
                    <p className="text-slate-800 dark:text-slate-100">
                      {selectedRequest.email}
                    </p>
                  </RequestInfoItem>
                  <RequestInfoItem label="Created">
                    <p className="text-slate-800 dark:text-slate-100">
                      {selectedRequest.createdAt}
                    </p>
                  </RequestInfoItem>
                </RequestInfoGrid>
              </RequestDetailsSection>
              <RequestDetailsSection title="Web application access">
                <RequestInfoGrid className="request-details-grid">
                  <RequestInfoItem label="Credential">
                    <p className="text-slate-800 dark:text-slate-100">
                      {selectedRequest.accessCredential}
                    </p>
                  </RequestInfoItem>
                  <RequestInfoItem label="Web URL">
                    <p className="mt-1 font-mono text-xs text-slate-800 dark:text-slate-100">
                      {selectedRequest.accessUrl}
                    </p>
                  </RequestInfoItem>
                </RequestInfoGrid>
              </RequestDetailsSection>
              <RequestDetailsSection title="Justification" variant="justification">
                <p className="text-slate-800 dark:text-slate-100">
                  {selectedRequest.requestMessage}
                </p>
              </RequestDetailsSection>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  )
}
